import { useCallback, useRef, useState, type CSSProperties } from 'react';
import { usePromptTimeTravel } from '../../hooks/usePromptTimeTravel';
import { StepMarker } from './StepMarker';
import { testIds } from '../../lib/testIds';

interface TimelineScrubberProps {
  sessionId: string;
  forkStepIndices?: number[];
}

export function TimelineScrubber({ sessionId, forkStepIndices = [] }: TimelineScrubberProps) {
  const { steps, currentStepIndex, highWaterMark, goToStep } = usePromptTimeTravel(sessionId);
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const forkSet = new Set(forkStepIndices);

  const handleTrackClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (steps.length === 0 || !trackRef.current) return;
      const rect = trackRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      const idx = Math.round(pct * (steps.length - 1));
      goToStep(idx);
    },
    [steps, goToStep],
  );

  const handleMouseDown = useCallback(() => setDragging(true), []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!dragging || steps.length === 0 || !trackRef.current) return;
      const rect = trackRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      const idx = Math.round(pct * (steps.length - 1));
      goToStep(idx);
    },
    [dragging, steps, goToStep],
  );

  const handleMouseUp = useCallback(() => setDragging(false), []);

  const containerStyle: CSSProperties = {
    padding: '8px 12px',
    fontFamily: "var(--xp-font-body)",
    fontSize: 12,
    userSelect: 'none',
  };

  const trackContainerStyle: CSSProperties = {
    position: 'relative',
    border: '2px solid',
    borderColor: '#808080 #dfdfdf #dfdfdf #808080',
    backgroundColor: '#ffffff',
    padding: '6px 8px',
    minHeight: 30,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  };

  const markersRowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    position: 'relative',
    width: '100%',
  };

  const highWaterStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: steps.length > 0 ? `${(Math.min(highWaterMark, steps.length) / steps.length) * 100}%` : '100%',
    width: 0,
    borderLeft: '2px dashed #808080',
    pointerEvents: 'none',
  };

  const labelStyle: CSSProperties = {
    marginTop: 4,
    textAlign: 'center',
    color: '#000000',
    fontFamily: "var(--xp-font-body)",
    fontSize: 11,
  };

  const emptyStyle: CSSProperties = {
    textAlign: 'center',
    color: '#808080',
    padding: '4px 0',
    fontStyle: 'italic',
    fontSize: 11,
  };

  if (steps.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={trackContainerStyle}>
          <div style={emptyStyle}>No steps recorded</div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle} data-testid={testIds.timeline.scrubber}>
      <div
        ref={trackRef}
        style={trackContainerStyle}
        onClick={handleTrackClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        data-testid={testIds.timeline.scrubberTrack}
      >
        <div style={markersRowStyle}>
          {steps.map((step, i) => (
            <StepMarker
              key={step.id}
              step={step}
              index={i}
              isCurrent={i === currentStepIndex}
              isForkPoint={forkSet.has(i)}
              onClick={() => goToStep(i)}
            />
          ))}
        </div>
        <div style={highWaterStyle} data-testid={testIds.timeline.highWaterMark} />
      </div>
      <div style={labelStyle} data-testid={testIds.timeline.stepLabel}>
        Step {currentStepIndex + 1} of {steps.length}
      </div>
    </div>
  );
}
